/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import './Login.css';
import {AuthInstance, getBestSupportedLocale} from '@exc/auth';
import Banner from '@react/react-spectrum/Banner';
import Button from '@react/react-spectrum/Button';
import {FormattedMessage, withLocalization} from '@adobe/adobe-intl-react';
import {getByName} from '../configs/icons';
import {GraphQlService} from '@exc/graphql';
import {history} from '@exc/router';
import Metrics from '@exc/metrics';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import React from 'react';
import Wait from '@react/react-spectrum/Wait';

const SUPPORTED_LOCALES = ['de-DE', 'en-US', 'es-ES', 'fr-FR', 'it-IT', 'ja-JP', 'ko-KR', 'pt-BR', 'zh-CN', 'zh-TW'];
/**
 * Login component.
 */
class Login extends React.Component {
  constructor(props) {
    super(props);
    this.environment = props.environment;
    this.handleSignInWithId = this.handleSignInWithId.bind(this);
    this.year = new Date().getFullYear();
    this.state = {
      loading: true
    };
    this.history = props.history || history;
    this.metrics = new Metrics('exc.core.modules.Login');
  }

  async componentDidMount() {
    document.title = 'Login | Adobe Experience Cloud';
    this.auth = await AuthInstance();

    if (!this.auth.isAuthenticated()) {
      this.setState({loading: false});
      this.metrics.event('Login.imsSSO.ready');
    } else {
      if (this.auth.isValidSession()) {
        const loginTimer = this.metrics.start('Login');
        await Promise.all([
          this.imsSSOhandler()
        ]);
        loginTimer.time('done');
      } else {
        this.setState({loading: false});
        this.auth.signOut();
        this.metrics.event('Login.imsSSO.ready', 'Invalid session: imsSSO login required');
      }
    }
  }

  /**
   * Async method to perform IMS SSO and update IMS Session data
   * with Current Org.
   * returns Landing Page and Current Org in response.
   */
  async imsSSOhandler() {
    const {redirectlocation} = this.props.query;

    // Get encoded hash from previous session in order to correctly redirect the user
    let oldHash;
    const {old_hash} = (redirectlocation && querystring.parse(redirectlocation)) || {};
    if (old_hash) {
      oldHash = `#/old_hash=${encodeURIComponent(old_hash)}`;
    }

    // Start timer for User Login Handling after getting redirected from SUSI Screen
    let paramRedirectLocation = redirectlocation && decodeURIComponent(redirectlocation);
    const redirectLocationURL = paramRedirectLocation && new URL(paramRedirectLocation);
    const redirectSearch = redirectLocationURL ? redirectLocationURL.search : '';

    //default path '/' handling should be taken care of login-service
    if (
      redirectLocationURL &&
      redirectLocationURL.pathname === '/' &&
      redirectLocationURL.search === '' &&
      (!redirectLocationURL.hash || redirectLocationURL.hash === '#/' || /^\/$/.test(redirectLocationURL.hash))
    ) {
      paramRedirectLocation = null;
    }

    let landingPageResponse = null;
    let shellInitData = null;
    try {
      const imsAccesstokenInfo = await this.auth.fetchAccessTokenInfo();
      const imsAccessToken = imsAccesstokenInfo && imsAccesstokenInfo.access_token;
      const expiryTimeInterval = imsAccesstokenInfo && imsAccesstokenInfo.expires_in;
      const imsUserProfile = this.auth.fetchUserProfile();
      const locale = getBestSupportedLocale(imsUserProfile, SUPPORTED_LOCALES);
      const initConfig = {
        apiKey: this.auth.getClientId(),
        config_version: 0,
        expiryTimeInterval: JSON.stringify(expiryTimeInterval),
        imsUserProfile: JSON.stringify(imsUserProfile),
        keys: ['lastClickTimestamp', 'lastSeenAnnouncementsTimestamp', 'lastNotificationSeenTimestamp'],
        locale,
        redirectLocation: paramRedirectLocation,
        userId: imsUserProfile.userId
      };
      let gqlservice = new GraphQlService(this.environment, this.auth.getClientId(), imsAccessToken);
      [landingPageResponse, shellInitData] = await Promise.all([
        this.auth.imsSSOhandler(paramRedirectLocation),
        gqlservice.initializeShellDataForLogin(initConfig)
      ]);
    } catch (error) {
      this.metrics.error('Failed to fetch Landing page: ', error);
    }

    if (!shellInitData) {
      this.metrics.error('GQL returned invalid/null response for Shell Init Data');
    }
    this.metrics.event('Login.redirect.ready');

    let landingPageUrlString = landingPageResponse && landingPageResponse.redirectLocation;
    if (this.props.redirectDisabled) {
      if (paramRedirectLocation === null) {
        this.history.push('/');
        this.props.onAuthComplete();
        return;
      } else {
        landingPageUrlString = paramRedirectLocation;
      }
    }

    if (landingPageUrlString) {
      const landingPageUrl = new URL(landingPageUrlString);
      // Identify if the redirect location is a route in exc-app or an external page.
      // Check to be improved further. Currently validating against first level route.
      if (landingPageUrl.origin !== window.location.origin) {
        // If user's landing page is ExC Home, take user to Unified Shell Home
        if (landingPageUrl.hostname.startsWith('exc-home') && landingPageUrl.pathname.startsWith('/exc-home/home')) {
          this.history.push({
            hash: oldHash,
            pathname: '/',
            search: redirectSearch
          });
        } else {
          window.location.assign(landingPageUrl);
        }
      } else {
        this.history.push({
          hash: oldHash || landingPageUrl.hash,
          pathname: '/',
          search: decodeURIComponent(landingPageUrl.search).split('#')[0]
        });
      }
    } else {
      this.history.push({pathname: '/', search: redirectSearch});
    }
    this.props.onAuthComplete();
  }

  handleSignInWithId() {
    if (!this.state.loading) {
      let imsRedirectUri = `${window.location.origin}/login`;
      if (this.props.querystring) {
        imsRedirectUri += `?${this.props.querystring}`;
      }
      this.auth.signIn(imsRedirectUri);
    }
  }

  // Display login page to sign-in or when the login route is explicitly hit
  render() {
    const {formatMessage} = this.props;

    // In case of an authenticated req & landed from a referrerUri,
    // show loader alone
    const showLoader = document.referrer;
    let waitLoader = <div />;

    if (this.state.loading) {
      waitLoader = <Wait centered className="login-wait-container" size="L" />;
      if (showLoader) {
        return <div style={{height: '100vh'}}>{waitLoader}</div>;
      }
    }

    const Icon = getByName('AdobeExperienceCloud');
    const excLeagueText = formatMessage({
      defaultMessage: 'Experience League: Get the most out of your products with guided learning, community and experts',
      description: 'Information Banner for Experience League',
      messageId: 'leagueBannerText'
    });

    return (
      <main>
        {waitLoader}
        <div className="login-background" />
        <img alt="Adobe Inc" className="login-adobe-logo" src="data:image/png;base64,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" />
        <div className="login-box">
          <div className="login-leftbox">
            <Icon className="login-expcloud-icon" size="L" />
            <h1 className="login-title">Adobe Experience Cloud</h1>
            <div className="login-sign-in">
              <Button
                className="login-sign-in-button"
                label={formatMessage({
                  defaultMessage: 'Sign In with an Adobe ID',
                  description: 'Label for button to sign in with Adobe ID',
                  messageId: 'signInWithId'
                })}
                onClick={this.handleSignInWithId}
                variant="cta"
              />
            </div>
          </div>
          <div className="notif-banner">
            <a
              aria-label={excLeagueText}
              href="http://landing.adobe.com/experience-league?promoid=6JMR7HJX&mv=other"
              rel="noopener noreferrer"
              target="_blank"
            >
              <Banner className="excleague-banner" content={excLeagueText} />
            </a>
          </div>
        </div>
        <ul className="login-usage-box">
          <li className="login-left-li">
            <a className="login-link" href="http://www.adobe.com/go/marketingcloud_privacy">
              <FormattedMessage
                defaultMessage="Privacy (UPDATED)"
                description="Label for link to privacy information"
                messageId="privacy"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="https://marketing.adobe.com/resources/help/en_US/terms.html">
              <FormattedMessage
                defaultMessage="Terms of Use"
                description="Label for link to Terms of Use"
                messageId="termsOfUse"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="http://www.adobe.com/in/privacy/cookies.html">
              <FormattedMessage
                defaultMessage="Cookies"
                description="Label for link to cookies information"
                messageId="cookies"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="https://helpx.adobe.com/marketing-cloud.html">
              <FormattedMessage
                defaultMessage="Help"
                description="Label for link to help"
                messageId="help"
              />
            </a>
          </li>
          <li className="login-right-li">{`© ${this.year} Adobe Systems Incorporated. All Rights Reserved.`}</li>
        </ul>
      </main>
    );
  }
}

Login.defaultProps = {
  onAuthComplete: function() {}
};

Login.propTypes = {
  onAuthComplete: PropTypes.func
};

export default withLocalization(Login);
